import React, { useState, useEffect } from 'react';
import { Award, Star, Play, X, Calendar, ArrowRight } from 'lucide-react';
import { team as fallbackTeam } from '../data/mock';
import axios from 'axios';
import { API } from '../config';
import BookingCalendar from './BookingCalendar';

const THERAPIST_IDS = ['michael', 'ana', 'buggie', 'leslie', 'lana', 'victor', 'eegi', 'mayara', 'rino', 'salome'];

// Set to the GoFundMe URL to show the "Support Ana" donate button on her card
const ANA_GOFUNDME_URL = 'https://www.gofundme.com/u/9ffb966a-d71a-4bc2-a47b-6481e2f87a34';
const therapistIdFor = (name) => {
  const words = name.toLowerCase().replace(/[^a-z\s]/g, '').split(/\s+/);
  return THERAPIST_IDS.find((id) => words.includes(id)) || 'no_preference';
};
const firstNameFor = (name) => (name.toLowerCase().startsWith('dr') ? 'Dr. Michael' : name.split(' ')[0]);

const TeamSection = () => {
  const [playingVideo, setPlayingVideo] = useState(null);
  const [team, setTeam] = useState(fallbackTeam);
  const [bookingTherapist, setBookingTherapist] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios.get(`${API}/team`)
      .then(res => { if (res.data?.length) setTeam(res.data); })
      .catch(() => {});
  }, []);

  return (
    <>
    <section id="team" className="py-28 bg-luxury-green relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-luxury-gold/5 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-luxury-gold/5 rounded-full blur-3xl" />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-luxury-gold text-xs uppercase tracking-[0.3em] font-medium">
            Our Therapists
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-alabaster mt-4 mb-6">
            Meet the
            <span className="block italic text-luxury-gold/80 mt-1">Expert Hands</span>
          </h2>
          <p className="text-alabaster-200/70 font-light leading-relaxed">
            Our qualified and experienced therapists bring years of expertise 
            and genuine care to every session.
          </p>
        </div>

        {/* Team Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {team.map((member) => (
            <div
              key={member.id}
              className="group bg-luxury-green-800 rounded-2xl overflow-hidden hover:shadow-2xl hover:shadow-black/40 transition-all duration-500 border border-luxury-gold/15 hover:border-luxury-gold/40 hover:-translate-y-1"
            >
              {/* Photo */}
              <div className="relative h-72 overflow-hidden">
                {member.image ? (
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 saturate-[0.92]"
                    style={{ 
                      objectPosition: member.imagePosition === 'center' 
                        ? 'center center' 
                        : member.imagePosition === 'crop-top'
                        ? 'center 15%'
                        : 'center top'
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-luxury-green-700 to-luxury-green-900 flex items-center justify-center">
                    <span className="font-display text-5xl text-luxury-gold/40">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-luxury-green-900/90 via-luxury-green-900/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-luxury-gold/50 to-transparent" />
                
                {member.video && (
                  <button
                    onClick={() => setPlayingVideo({ url: member.video, name: member.name })}
                    className="absolute bottom-4 right-4 bg-luxury-gold/90 hover:bg-luxury-gold text-luxury-green-900 px-4 py-2 rounded-full flex items-center gap-2 shadow-lg transition-all hover:scale-105 text-xs uppercase tracking-wider font-semibold"
                    data-testid={`play-video-${member.id}`}
                  >
                    <Play size={14} className="fill-luxury-green-900" />
                    Watch
                  </button>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="mb-3">
                  <h3 className="font-display text-lg text-alabaster">
                    {member.name}
                  </h3>
                  <p className="text-xs text-luxury-gold uppercase tracking-[0.15em]">{member.role}</p>
                </div>

                {/* Ana — Ireland Masters Athletics ribbon */}
                {member.name === 'Ana' && (
                  <div className="mb-4 bg-luxury-green-800/60 border border-luxury-gold/40 rounded-xl p-3" data-testid="support-ana-ribbon">
                    <p className="text-alabaster text-sm font-medium">
                      🇮🇪 Representing Ireland at the World Masters Athletics Championships — Daegu, South Korea 🇰🇷
                    </p>
                    <p className="text-luxury-gold text-xs mt-1 font-semibold">🥇🥇 100m & 200m GOLD — Irish Masters National Championships</p>
                    <p className="text-luxury-gold/90 text-xs mt-1">100m · 200m · 4×100m · 4×400m relays · Coached by Dr. Michael Omakobia</p>
                    {ANA_GOFUNDME_URL && (
                      <a
                        href={ANA_GOFUNDME_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-2 bg-luxury-gold hover:bg-luxury-gold/90 text-luxury-green-900 text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-full transition-transform hover:scale-105"
                        data-testid="support-ana-btn"
                      >
                        Support Ana's Journey 💚
                      </a>
                    )}
                  </div>
                )}

                {/* Specialties */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {member.specialties?.slice(0, 4).map((specialty) => (
                    <span
                      key={specialty}
                      className="px-2.5 py-1 bg-luxury-gold/10 text-luxury-gold/90 border border-luxury-gold/20 text-[10px] uppercase tracking-wider rounded-full"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>

                {/* Photo gallery strip */}
                {member.photos?.length > 0 && (
                  <div className="flex gap-2 mb-4" data-testid={`photo-strip-${therapistIdFor(member.name)}`}>
                    {member.photos.slice(0, 3).map((photo, i) => (
                      <button
                        key={photo}
                        onClick={() => setLightbox({ src: photo, name: member.name })}
                        className="w-16 h-16 rounded-lg overflow-hidden border border-luxury-gold/25 hover:border-luxury-gold/70 transition-all hover:scale-105"
                        data-testid={`photo-thumb-${therapistIdFor(member.name)}-${i}`}
                      >
                        <img src={photo} alt={`${member.name} at the studio`} className="w-full h-full object-cover" loading="lazy" />
                      </button>
                    ))}
                  </div>
                )}

                {/* Video gallery strip */}
                {member.videos?.length > 0 && (
                  <div className="flex gap-2 mb-4" data-testid={`video-strip-${therapistIdFor(member.name)}`}>
                    {member.videos.slice(0, 4).map((vid, i) => (
                      <button
                        key={vid}
                        onClick={() => setPlayingVideo({ url: vid, name: member.name })}
                        className="relative w-16 h-16 rounded-lg overflow-hidden border border-luxury-gold/25 hover:border-luxury-gold/70 transition-all hover:scale-105"
                        data-testid={`video-thumb-${therapistIdFor(member.name)}-${i}`}
                      >
                        <video src={`${vid}#t=0.5`} preload="metadata" muted playsInline className="w-full h-full object-cover" />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Play size={16} className="text-luxury-gold fill-luxury-gold" />
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => setBookingTherapist(therapistIdFor(member.name))}
                  className="w-full inline-flex items-center justify-center gap-2 bg-luxury-gold hover:bg-luxury-gold-600 text-luxury-green-900 rounded-full px-4 py-2.5 text-xs uppercase tracking-wider font-semibold transition-colors group/book"
                  data-testid={`book-with-${therapistIdFor(member.name)}`}
                >
                  <Calendar size={13} />
                  Book with {firstNameFor(member.name)}
                  <ArrowRight size={13} className="group-hover/book:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Therapists Note */}
        <div className="text-center mt-12">
          <p className="text-alabaster-200/60">
            We also have additional qualified male and female therapists available.
          </p>
        </div>
      </div>

      {/* Photo Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="photo-lightbox"
        >
          <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-3 -right-3 z-10 bg-luxury-gold text-luxury-green-900 p-2 rounded-full shadow-lg transition-transform hover:scale-110"
              data-testid="close-photo-lightbox"
            >
              <X size={20} />
            </button>
            <img src={lightbox.src} alt={lightbox.name} className="w-full max-h-[80vh] object-contain rounded-2xl" />
            <p className="text-center text-alabaster mt-3 font-display text-lg">{lightbox.name}</p>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {playingVideo && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPlayingVideo(null)}
        >
          <div 
            className="relative max-w-3xl w-full bg-black rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPlayingVideo(null)}
              className="absolute top-4 right-4 z-10 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
              data-testid="close-video-modal"
            >
              <X size={24} />
            </button>
            <video
              src={playingVideo.url}
              controls
              autoPlay
              className="w-full max-h-[80vh]"
            >
              Your browser does not support the video tag.
            </video>
            <div className="p-4 bg-stone-900 text-white">
              <h4 className="font-semibold">{playingVideo.name}</h4>
              <p className="text-sm text-stone-400">Dublin Holistic Massage therapist</p>
            </div>
          </div>
        </div>
      )}
    </section>

    <BookingCalendar
      isOpen={!!bookingTherapist}
      onClose={() => setBookingTherapist(null)}
      initialTherapist={bookingTherapist}
    />
    </>
  );
};

export default TeamSection;
